import { Head, Link, Form as InertiaForm, usePage } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface TaxDefinitionType {
  id: string
  name: string
  slug: string
  status: string
  description?: string
  tax_category_id: string
}

interface TaxRuleType {
  id: string
  name: string
  code?: string
  status: string
  formula_type: string
  valid_for: number
  logic_data?: Record<string, unknown>
}

interface ShowProps {
  tax_definition: TaxDefinitionType
  tax_rules: TaxRuleType[]
}

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const

const formulaOptions = [
  { value: 'flat_rate', label: 'Flat rate' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'size_based', label: 'Size based' },
] as const

export default function Show({ tax_definition, tax_rules }: ShowProps) {
  const { flash } = usePage()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [status, setStatus] = useState('active')
  const [formulaType, setFormulaType] = useState('flat_rate')
  const [amount, setAmount] = useState('')
  const [label, setLabel] = useState('')
  const [minAmount, setMinAmount] = useState('')

  useEffect(() => {
    if (flash?.notice) {
      setDialogOpen(false)
    }
  }, [flash?.notice])

  useEffect(() => {
    setAmount('')
    setLabel('')
    setMinAmount('')
  }, [formulaType])

  const logicData = useMemo(() => {
    if (formulaType === 'flat_rate') {
      return JSON.stringify({ amount: amount || null })
    }
    if (formulaType === 'size_based') {
      return JSON.stringify({ label: label || null, min_amount: minAmount || null })
    }
    return JSON.stringify({ label: label || null })
  }, [amount, label, minAmount, formulaType])

  return (
    <>
      <Head title={`Tax definition ${tax_definition.name}`} />
      <div className="mx-auto md:w-2/3 w-full px-8 pt-8">
        <h1 className="font-bold text-4xl">Tax definition</h1>

        <div className="mt-6 space-y-4">
          <div>
            <div className="text-sm text-muted-foreground">Name</div>
            <div className="text-lg">{tax_definition.name}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Slug</div>
            <div className="text-lg">{tax_definition.slug}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Status</div>
            <div className="text-lg">{tax_definition.status}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Description</div>
            <div className="text-lg">
              {tax_definition.description || '-'}
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">Tax rules</h2>
            <Button onClick={() => setDialogOpen(true)}>Add rule</Button>
          </div>
          <div className="mt-4 space-y-4">
            {tax_rules.map((rule) => (
              <div key={rule.id} className="rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{rule.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {rule.formula_type} • {rule.status}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Valid for: {rule.valid_for} year(s)
                  </div>
                </div>
                {rule.logic_data && Object.keys(rule.logic_data).length > 0 && (
                  <pre className="mt-2 rounded-md bg-muted p-3 text-xs">
                    {JSON.stringify(rule.logic_data, null, 2)}
                  </pre>
                )}
              </div>
            ))}
            {tax_rules.length === 0 && (
              <div className="text-sm text-muted-foreground">
                No tax rules yet.
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <Link
            href={`/tax_categories/${tax_definition.tax_category_id}`}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Back to tax category
          </Link>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New tax rule</DialogTitle>
          </DialogHeader>
          <InertiaForm
            transform={(data) => ({ tax_rule: data })}
            method="post"
            action={`/tax_definitions/${tax_definition.id}/tax_rules`}
            className="space-y-4"
            onSuccess={() => setDialogOpen(false)}
          >
            {({ errors, processing }) => (
              <>
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" placeholder="Rule name" />
                  {errors.name && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.name.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="formula_type">Formula type</Label>
                  <input type="hidden" name="formula_type" value={formulaType} />
                  <Select value={formulaType} onValueChange={setFormulaType}>
                    <SelectTrigger id="formula_type">
                      <SelectValue placeholder="Formula type" />
                    </SelectTrigger>
                    <SelectContent>
                      {formulaOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.formula_type && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.formula_type.join(', ')}
                    </div>
                  )}
                </div>
                <input type="hidden" name="logic_data" value={logicData} />
                {formulaType === 'flat_rate' && (
                  <div>
                    <Label htmlFor="amount">Amount</Label>
                    <Input
                      id="amount"
                      name="amount"
                      type="number"
                      value={amount}
                      onChange={(event) => setAmount(event.target.value)}
                      placeholder="0"
                    />
                  </div>
                )}
                {formulaType === 'size_based' && (
                  <>
                    <div>
                      <Label htmlFor="label">Label</Label>
                      <Input
                        id="label"
                        name="label"
                        value={label}
                        onChange={(event) => setLabel(event.target.value)}
                        placeholder="Size label"
                      />
                    </div>
                    <div>
                      <Label htmlFor="min_amount">Min amount</Label>
                      <Input
                        id="min_amount"
                        name="min_amount"
                        type="number"
                        value={minAmount}
                        onChange={(event) => setMinAmount(event.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </>
                )}
                {formulaType === 'percentage' && (
                  <div>
                    <Label htmlFor="label">Label</Label>
                    <Input
                      id="label"
                      name="label"
                      value={label}
                      onChange={(event) => setLabel(event.target.value)}
                      placeholder="Percentage label"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="status">Status</Label>
                  <input type="hidden" name="status" value={status} />
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.status && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.status.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="valid_for">Valid for (years)</Label>
                  <Input
                    id="valid_for"
                    name="valid_for"
                    type="number"
                    min="1"
                    defaultValue="1"
                  />
                  {errors.valid_for && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.valid_for.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="logic_data">Logic data (JSON)</Label>
                  <Textarea
                    id="logic_data"
                    name="logic_data"
                    placeholder='{"rate": 10}'
                  />
                  {errors.logic_data && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.logic_data.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={processing}>
                    Create rule
                  </Button>
                </div>
              </>
            )}
          </InertiaForm>
        </DialogContent>
      </Dialog>
    </>
  )
}
